import { useState, useEffect, useCallback } from 'react';
import { isAxiosError } from 'axios';
import { loginApi } from '../services/api';
import { getAppEntryUrl } from '../lib/utils';
import type { AppInfo, AuthResponse, LoginSession } from '../types/auth';

interface LoginResult {
  success: boolean;
  hasExistingSession?: boolean;
  sessionToken?: string;
  error?: string;
  errorCode?: string;
  apps?: AppInfo[];
  session?: LoginSession;
  /** AUTH_PASSWORD_UPDATE_REQUIRED: the password must be changed through the reset page first */
  passwordUpdateRequired?: boolean;
  forgetPassTokenUser?: string;
}

export function useAuth() {
  const [token, setToken] = useState<string | null>(null);
  const [apps, setApps] = useState<AppInfo[]>([]);

  useEffect(() => {
    const savedToken = localStorage.getItem('access_token');
    const savedApps = localStorage.getItem('apps');

    if (savedToken) {
      setToken(savedToken);
    }
    if (savedApps) {
      try {
        setApps(JSON.parse(savedApps));
      } catch {
        setApps([]);
      }
    }
  }, []);

  const handleResponse = useCallback((response: AuthResponse): LoginResult => {
    if (response.errorCode) {
      return { success: false, error: response.message, errorCode: response.errorCode };
    }

    if (response.sessionToken) {
      return {
        success: false,
        hasExistingSession: true,
        sessionToken: response.sessionToken,
      };
    }

    if (!response.access_token) return { success: false };

    // Left by earlier versions: it held a URL carrying the access token
    localStorage.removeItem('last_session');

    setToken(response.access_token);
    setApps(response.apps || []);
    localStorage.setItem('apps', JSON.stringify(response.apps || []));
    if (response.refresh_token) {
      // Keycloak session: kept in memory only, the app receives its own tokens through a one-time code
      localStorage.removeItem('access_token');
    } else {
      localStorage.setItem('access_token', response.access_token);
    }

    return {
      success: true,
      apps: response.apps,
      session: { access_token: response.access_token, refresh_token: response.refresh_token },
    };
  }, []);

  const callLogin = useCallback(async (credentials: { username: string; password: string; force?: boolean }) => {
    try {
      return handleResponse(await loginApi(credentials));
    } catch (error) {
      // 403 AUTH_PASSWORD_UPDATE_REQUIRED: current password refused by the new security policy
      const data = isAxiosError(error) ? (error.response?.data as AuthResponse | undefined) : undefined;
      if (data?.errorCode === 'AUTH_PASSWORD_UPDATE_REQUIRED' && data.forgetPassTokenUser) {
        return { success: false, passwordUpdateRequired: true, forgetPassTokenUser: data.forgetPassTokenUser, errorCode: data.errorCode };
      }
      throw error;
    }
  }, [handleResponse]);

  const login = useCallback(
    (username: string, password: string) => callLogin({ username, password }),
    [callLogin],
  );

  const expireAndRelogin = useCallback(
    (_sessionToken: string, username: string, password: string) => callLogin({ username, password, force: true }),
    [callLogin],
  );

  return { token, apps, login, expireAndRelogin };
}

/** Opens the only app of the user; false when there is none (or several: the app selector handles it). */
export async function redirectAfterLogin(session: LoginSession, apps?: AppInfo[]): Promise<boolean> {
  if (apps && apps.length === 1) {
    window.location.href = await getAppEntryUrl(apps[0], session);
    return true;
  }
  return false;
}
